// Libs
import classNames from 'classnames/bind';
import { uid } from 'uid';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { useContext, forwardRef, useEffect } from 'react';
import { Avatar, Box, IconButton, Stack, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { SimpleTreeItemWrapper, TreeItemComponentProps, TreeItems } from 'dnd-kit-sortable-tree';
// Components, Layouts, Pages
import { BaseCheckbox, BaseInput } from '~/components/common';
// Others
import { treeItemPackageProductSchema } from './helper';
import { TypeListInventory } from '~/utils/enum/common';
import { formatCurrencyUSD } from '~/utils/helper/common';
import { EMPTY_STRING, REG_EXP } from '~/utils/constants/common';
import { IContextTreeItems, ITreeItem } from '~/utils/interface/common';
import { IFormPackage, IPackageProduct } from '~/utils/interface/package';
import GroupContext from '~/context/groupContext/GroupContext';
// Styles, images, icons
import styles from './ItemPackageProduct.module.scss';
import './ItemPackageProduct.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const ItemPackageProduct = forwardRef<HTMLDivElement, TreeItemComponentProps<ITreeItem<IPackageProduct>>>(
  (props, ref) => {
    //#region Destructuring Props
    const { item, onRemove } = props;
    const wrapperProps = {
      ...props,
      showDragHandle: true,
      handleProps: undefined,
    };
    //#endregion Destructuring Props

    //#region Declare Hook
    const { t } = useTranslation(['admin/packages', 'common/base']);
    const { items, setItems } = useContext<IContextTreeItems<IPackageProduct>>(GroupContext);

    const {
      control,
      setValue,
      watch,
      getValues,
      formState: { errors },
    } = useForm<ITreeItem<IPackageProduct>>({
      resolver: yupResolver(treeItemPackageProductSchema(t)),
      defaultValues: item,
    });

    const { setValue: setValuePackage, watch: watchPackage } = useFormContext<IFormPackage>();

    const { data } = watch();
    const quantity = watch('data.quantity');
    const rentalPrice = watch('data.rentalPrice');
    const isRequired = watch('data.isRequired');
    const { deleteItemIds } = watchPackage();
    //#endregion Declare Hook

    //#region Selector
    //#endregion Selector

    //#region Declare State
    //#endregion Declare State

    //#region Declare Memo
    //#endregion Declare Memo

    //#region Implement Hook
    useEffect(() => {
      setValue('data.subtotal', Number(quantity) * Number(rentalPrice));
    }, [quantity]);
    //#endregion Implement Hook

    //#region Handle Function
    const handleSetItem = () => {
      setItems(handleUpdateItemById(items, item.id, getValues()));
    };

    const handleUpdateItemById = (
      items: TreeItems<ITreeItem<IPackageProduct>>,
      targetId: string,
      newItem: ITreeItem<IPackageProduct>
    ): TreeItems<ITreeItem<IPackageProduct>> => {
      return items.map((item: ITreeItem<IPackageProduct>) => {
        if (item.id === targetId) {
          return newItem;
        } else if (item.children) {
          return {
            ...item,
            children: handleUpdateItemById(item.children, targetId, newItem),
          };
        } else {
          return item;
        }
      });
    };

    const handleRemove = () => {
      setValuePackage('deleteProduct', uid());
      setValuePackage('selectQuantitySetProduct', 0);
      setValuePackage('selectIdProductSetProduct', data?.inventoryId);
      setValuePackage('selectIdVariantSetProduct', data?.variantId);
      data?.id && setValuePackage('deleteItemIds', [...(deleteItemIds || []), data?.id]);

      onRemove?.();
    };
    //#endregion Handle Function

    return (
      <Box id='itemPackageProductComponent' key={item.id}>
        <SimpleTreeItemWrapper {...wrapperProps} ref={ref} disableCollapseOnItemClick showDragHandle={false}>
          <Table stickyHeader>
            <TableBody>
              <TableRow hover>
                <TableCell width={'28%'}>
                  <Stack direction={'row'} gap={3} alignItems={'center'}>
                    <IconButton {...props.handleProps} className={cx('iconDrag')}>
                      <img src={icons.commonIconRemove} alt={t('common_alt_img', { ns: 'common/base' })} />
                    </IconButton>

                    <Avatar
                      src={
                        data?.inventoryImages && data?.inventoryImages?.length > 0
                          ? data?.inventoryImages[0].url
                          : EMPTY_STRING
                      }
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      variant='rounded'
                    />

                    <Stack gap={'2px'}>
                      <Box className={cx('textCell', 'viewRow')}>{data?.name}</Box>

                      {data?.variantName && <Box className={cx('textVariant')}>{data?.variantName}</Box>}
                    </Stack>

                    {data?.type === TypeListInventory.PART && (
                      <Box className={cx('addOne')}>{t('table_value_title_add_on')}</Box>
                    )}
                  </Stack>
                </TableCell>

                <TableCell width={'15%'}>
                  <Box className={cx('textCell', 'viewRow')}>
                    {formatCurrencyUSD({ value: data?.rentalPrice, showDecimal: true })}
                  </Box>
                </TableCell>

                <TableCell width={'15%'} align='center'>
                  <Controller
                    name={`data.quantity`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Stack direction='row' spacing={1} alignItems='center' gap={2} className={cx('viewQuantity')}>
                        <IconButton
                          className={cx('buttonQty')}
                          onClick={() => {
                            const newQty = Math.max(Number(value) - 1, 0);
                            setValue(`data.quantity`, newQty, { shouldDirty: true });
                            setValuePackage('selectQuantitySetProduct', newQty || 0, { shouldDirty: true });
                            setValuePackage('selectIdProductSetProduct', data?.inventoryId, { shouldDirty: true });
                            setValuePackage('selectIdVariantSetProduct', data?.variantId, { shouldDirty: true });
                            handleSetItem();
                          }}
                        >
                          <img src={icons.commonIconMinus} alt={t('common_alt_img')} />
                        </IconButton>

                        <BaseInput
                          height={31}
                          textAlignCenter
                          width={55}
                          borderRadius={'4px'}
                          value={String(value)}
                          onChange={(e) => {
                            const inputValue = e.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                            const numericValue = inputValue ? Number(inputValue) : EMPTY_STRING;
                            onChange(numericValue);
                            setValuePackage('selectQuantitySetProduct', numericValue || 0, { shouldDirty: true });
                            setValuePackage('selectIdProductSetProduct', data?.inventoryId, { shouldDirty: true });
                            setValuePackage('selectIdVariantSetProduct', data?.variantId, { shouldDirty: true });
                            handleSetItem();
                          }}
                          errorMessage={errors?.data?.quantity?.message}
                        />

                        <IconButton
                          className={cx('buttonQty')}
                          onClick={() => {
                            const newQty = Number(value) + 1;
                            setValue(`data.quantity`, newQty, { shouldDirty: true });
                            setValuePackage('selectQuantitySetProduct', newQty || 0, { shouldDirty: true });
                            setValuePackage('selectIdProductSetProduct', data?.inventoryId, { shouldDirty: true });
                            setValuePackage('selectIdVariantSetProduct', data?.variantId, { shouldDirty: true });
                            handleSetItem();
                          }}
                        >
                          <img src={icons.commonIconPlusGrey} alt={t('common_alt_img')} />
                        </IconButton>
                      </Stack>
                    )}
                  />
                </TableCell>

                <TableCell width={'15%'}>
                  <Box className={cx('textCell', 'viewRow')}>
                    {formatCurrencyUSD({ value: data?.subtotal, showDecimal: true })}
                  </Box>
                </TableCell>

                <TableCell width={'12%'}>
                  <Controller
                    name={`data.isRequired`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <BaseCheckbox
                        checked={value}
                        onChange={(checked) => {
                          onChange(checked);
                          handleSetItem();
                        }}
                        label={t('package_form_set_product_table_title_required_checkbox')}
                      />
                    )}
                  />
                </TableCell>

                {/* <TableCell width={'13%'}>
                  <Controller
                    name={`data.isShowPrice`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <BaseCheckbox
                        checked={value}
                        onChange={(checked) => {
                          onChange(checked);
                          handleSetItem();
                        }}
                        label={t('package_form_set_product_table_title_show_product')}
                      />
                    )}
                  />
                </TableCell> */}

                <TableCell width={'5%'} align='center'>
                  <IconButton className={cx('buttonIcon')} onClick={handleRemove}>
                    <img
                      className={cx('iconDelete')}
                      src={icons.commonIconTrash}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                    />
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </SimpleTreeItemWrapper>
      </Box>
    );
  }
);

export default ItemPackageProduct;
